/**
 * Home PR Spotlight
 * Handles card carousel autoplay, delayed viewport start, and logo syncing.
 */
(function() {
  'use strict';

  var sectionInstances = new Map();
  var prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function toNumber(value, fallback) {
    var parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function getVisibleCount(swiper) {
    if (!swiper || !swiper.params) return 1;
    var slidesPerView = swiper.params.slidesPerView;

    if (slidesPerView === 'auto' && typeof swiper.slidesPerViewDynamic === 'function') {
      slidesPerView = swiper.slidesPerViewDynamic();
    }

    slidesPerView = toNumber(slidesPerView, 1);
    return Math.max(1, Math.floor(slidesPerView));
  }

  function updateNavVisibility(swiper, carouselEl, totalSlides) {
    var prevBtn = carouselEl.querySelector('.swiper-button-prev');
    var nextBtn = carouselEl.querySelector('.swiper-button-next');
    if (!prevBtn || !nextBtn || !swiper) return;

    if (totalSlides <= getVisibleCount(swiper)) {
      prevBtn.style.setProperty('display', 'none', 'important');
      nextBtn.style.setProperty('display', 'none', 'important');
    } else {
      prevBtn.style.removeProperty('display');
      nextBtn.style.removeProperty('display');
    }
  }

  function scrollLogoTrackToLogo(track, logo) {
    if (!track || !logo || track.scrollWidth <= track.clientWidth) return;

    var currentLeft = track.scrollLeft;
    var trackWidth = track.clientWidth;
    var logoLeft = logo.offsetLeft;
    var logoRight = logoLeft + logo.offsetWidth;
    var targetLeft = currentLeft;

    if (logoLeft < currentLeft) {
      targetLeft = logoLeft;
    } else if (logoRight > currentLeft + trackWidth) {
      targetLeft = logoRight - trackWidth;
    }

    if (targetLeft === currentLeft) return;

    track.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: prefersReducedMotion ? 'auto' : 'smooth'
    });
  }

  function updateLogoState(state) {
    var swiper = state.swiper;
    if (!swiper || swiper.destroyed) return;

    var activeIndex = typeof swiper.realIndex === 'number' ? swiper.realIndex : swiper.activeIndex;
    var visibleCount = getVisibleCount(swiper);
    var activeIndexes = [];

    for (var i = 0; i < visibleCount; i += 1) {
      if (activeIndex + i < state.totalSlides) {
        activeIndexes.push(activeIndex + i);
      }
    }

    state.logoButtons.forEach(function(button) {
      var logoIndex = parseInt(button.dataset.index, 10);
      var isActive = activeIndexes.indexOf(logoIndex) !== -1;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    var firstActiveLogo = state.logoButtons[activeIndexes[0]];
    scrollLogoTrackToLogo(state.logoTrack, firstActiveLogo);
  }

  function controlAutoplay(state) {
    var swiper = state.swiper;
    if (!swiper || swiper.destroyed || !swiper.autoplay) return;

    var shouldRun = state.autoplayEnabled &&
      state.isInView &&
      !state.isHovering &&
      !state.hasFocus &&
      !state.isTouching &&
      !document.hidden &&
      !prefersReducedMotion;

    if (shouldRun) {
      swiper.autoplay.start();
    } else {
      swiper.autoplay.stop();
    }
  }

  function bindPauseEvents(sectionEl, state) {
    var onMouseEnter = function() {
      state.isHovering = true;
      controlAutoplay(state);
    };
    var onMouseLeave = function() {
      state.isHovering = false;
      controlAutoplay(state);
    };
    var onFocusIn = function() {
      state.hasFocus = true;
      controlAutoplay(state);
    };
    var onFocusOut = function() {
      window.setTimeout(function() {
        state.hasFocus = sectionEl.contains(document.activeElement);
        controlAutoplay(state);
      }, 0);
    };
    var onPointerDown = function() {
      state.isTouching = true;
      controlAutoplay(state);
    };
    var onPointerUp = function() {
      state.isTouching = false;
      controlAutoplay(state);
    };
    var onVisibilityChange = function() {
      controlAutoplay(state);
    };

    sectionEl.addEventListener('mouseenter', onMouseEnter);
    sectionEl.addEventListener('mouseleave', onMouseLeave);
    sectionEl.addEventListener('focusin', onFocusIn);
    sectionEl.addEventListener('focusout', onFocusOut);
    sectionEl.addEventListener('pointerdown', onPointerDown, { passive: true });
    sectionEl.addEventListener('pointerup', onPointerUp, { passive: true });
    sectionEl.addEventListener('pointercancel', onPointerUp, { passive: true });
    sectionEl.addEventListener('touchend', onPointerUp, { passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);

    state.destroyCallbacks.push(function() {
      sectionEl.removeEventListener('mouseenter', onMouseEnter);
      sectionEl.removeEventListener('mouseleave', onMouseLeave);
      sectionEl.removeEventListener('focusin', onFocusIn);
      sectionEl.removeEventListener('focusout', onFocusOut);
      sectionEl.removeEventListener('pointerdown', onPointerDown);
      sectionEl.removeEventListener('pointerup', onPointerUp);
      sectionEl.removeEventListener('pointercancel', onPointerUp);
      sectionEl.removeEventListener('touchend', onPointerUp);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    });
  }

  function bindLogoClicks(state) {
    state.logoButtons.forEach(function(button) {
      var onClick = function() {
        if (state.logoClickSuppressed) {
          state.logoClickSuppressed = false;
          return;
        }

        var index = parseInt(button.dataset.index, 10);
        if (!state.swiper || state.swiper.destroyed || Number.isNaN(index)) return;

        if (state.swiper.params.loop && typeof state.swiper.slideToLoop === 'function') {
          state.swiper.slideToLoop(index);
        } else {
          state.swiper.slideTo(index);
        }
      };

      button.addEventListener('click', onClick);
      state.destroyCallbacks.push(function() {
        button.removeEventListener('click', onClick);
      });
    });
  }

  function bindLogoTrackDrag(state) {
    var track = state.logoTrack;
    if (!track || !window.PointerEvent) return;

    var isPointerDown = false;
    var isDragging = false;
    var startX = 0;
    var startScrollLeft = 0;
    var activePointerId = null;

    function canDrag() {
      return track.scrollWidth > track.clientWidth;
    }

    function releasePointer() {
      if (activePointerId !== null && typeof track.releasePointerCapture === 'function') {
        try {
          track.releasePointerCapture(activePointerId);
        } catch (error) {
          // Pointer capture may already be released by the browser.
        }
      }
      activePointerId = null;
    }

    function endDrag() {
      if (!isPointerDown) return;

      if (isDragging) {
        state.logoClickSuppressed = true;
        window.setTimeout(function() {
          state.logoClickSuppressed = false;
        }, 120);
      }

      isPointerDown = false;
      isDragging = false;
      track.classList.remove('is-dragging');
      releasePointer();
    }

    var onPointerDown = function(event) {
      if (event.pointerType !== 'mouse' || event.button !== 0 || !canDrag()) return;

      isPointerDown = true;
      isDragging = false;
      startX = event.clientX;
      startScrollLeft = track.scrollLeft;
      activePointerId = event.pointerId;

      if (typeof track.setPointerCapture === 'function') {
        try {
          track.setPointerCapture(activePointerId);
        } catch (error) {
          activePointerId = null;
        }
      }
    };

    var onPointerMove = function(event) {
      if (!isPointerDown) return;

      var deltaX = event.clientX - startX;
      if (!isDragging && Math.abs(deltaX) < 4) return;

      isDragging = true;
      track.classList.add('is-dragging');
      track.scrollLeft = startScrollLeft - deltaX;
      event.preventDefault();
    };

    var onClickCapture = function(event) {
      if (!state.logoClickSuppressed) return;

      state.logoClickSuppressed = false;
      event.preventDefault();
      event.stopPropagation();
    };

    track.addEventListener('pointerdown', onPointerDown);
    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('mouseleave', endDrag);
    track.addEventListener('click', onClickCapture, true);

    state.destroyCallbacks.push(function() {
      track.removeEventListener('pointerdown', onPointerDown);
      track.removeEventListener('pointermove', onPointerMove);
      track.removeEventListener('pointerup', endDrag);
      track.removeEventListener('pointercancel', endDrag);
      track.removeEventListener('mouseleave', endDrag);
      track.removeEventListener('click', onClickCapture, true);
      track.classList.remove('is-dragging');
    });
  }

  function setupIntersectionObserver(sectionEl, state) {
    if (!('IntersectionObserver' in window)) {
      state.isInView = true;
      controlAutoplay(state);
      return;
    }

    var startOffset = Math.max(0, Math.min(90, toNumber(sectionEl.dataset.startOffset, 30)));
    state.observer = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        state.isInView = entry.isIntersecting;
        controlAutoplay(state);
      });
    }, {
      root: null,
      rootMargin: '0px 0px -' + startOffset + '% 0px',
      threshold: 0
    });

    state.observer.observe(sectionEl);
    state.destroyCallbacks.push(function() {
      if (state.observer) {
        state.observer.disconnect();
      }
    });
  }

  function initSection(sectionEl) {
    var sectionId = sectionEl.dataset.sectionId;
    if (!sectionId || sectionInstances.has(sectionId)) return;

    var carouselEl = sectionEl.querySelector('[data-home-pr-swiper]');
    if (!carouselEl || typeof Swiper === 'undefined') return;

    var slides = carouselEl.querySelectorAll('.swiper-slide');
    var totalSlides = slides.length;
    if (!totalSlides) return;

    var logoTrack = sectionEl.querySelector('[data-home-pr-logo-track]');
    var logoButtons = Array.prototype.slice.call(sectionEl.querySelectorAll('[data-home-pr-logo]'));
    var autoplayDelay = parseInt(sectionEl.dataset.autoplayDelay, 10) || 3000;
    var autoplayEnabled = sectionEl.dataset.autoplay === 'true' && totalSlides > 1;
    var mobileSlides = toNumber(sectionEl.dataset.mobileSlides, 1.15);
    var desktopSlides = toNumber(sectionEl.dataset.desktopSlides, 2.2);
    var tabletSlides = Math.min(desktopSlides, 2.2);

    var state = {
      sectionEl: sectionEl,
      carouselEl: carouselEl,
      logoTrack: logoTrack,
      logoButtons: logoButtons,
      totalSlides: totalSlides,
      autoplayEnabled: autoplayEnabled,
      isInView: false,
      isHovering: false,
      hasFocus: false,
      isTouching: false,
      logoClickSuppressed: false,
      observer: null,
      swiper: null,
      destroyCallbacks: []
    };

    var swiper = new Swiper(carouselEl, {
      loop: false,
      watchOverflow: true,
      slidesPerGroup: 1,
      spaceBetween: 16,
      watchSlidesProgress: true,
      observer: true,
      observeParents: true,
      simulateTouch: true,
      allowTouchMove: true,
      touchRatio: 1,
      resistance: true,
      resistanceRatio: 0.85,
      threshold: 5,
      centeredSlides: false,
      updateOnWindowResize: true,
      resizeObserver: true,
      speed: 600,
      autoplay: autoplayEnabled ? {
        delay: autoplayDelay,
        disableOnInteraction: false,
        pauseOnMouseEnter: true
      } : false,
      navigation: {
        nextEl: carouselEl.querySelector('.swiper-button-next'),
        prevEl: carouselEl.querySelector('.swiper-button-prev')
      },
      a11y: {
        enabled: true,
        prevSlideMessage: sectionEl.dataset.prevLabel || 'Previous media review',
        nextSlideMessage: sectionEl.dataset.nextLabel || 'Next media review'
      },
      breakpoints: {
        320: {
          slidesPerView: mobileSlides,
          spaceBetween: 16
        },
        768: {
          slidesPerView: tabletSlides,
          spaceBetween: 18
        },
        1025: {
          slidesPerView: desktopSlides,
          spaceBetween: 20
        }
      },
      on: {
        init: function() {
          if (this.autoplay) {
            this.autoplay.stop();
          }
          updateNavVisibility(this, carouselEl, totalSlides);
          updateLogoState(state);
        },
        slideChange: function() {
          updateNavVisibility(this, carouselEl, totalSlides);
          updateLogoState(state);
        },
        reachBeginning: function() {
          updateNavVisibility(this, carouselEl, totalSlides);
        },
        reachEnd: function() {
          updateNavVisibility(this, carouselEl, totalSlides);
        },
        fromEdge: function() {
          updateNavVisibility(this, carouselEl, totalSlides);
        },
        breakpoint: function() {
          updateNavVisibility(this, carouselEl, totalSlides);
          updateLogoState(state);
        },
        resize: function() {
          updateNavVisibility(this, carouselEl, totalSlides);
          updateLogoState(state);
        }
      }
    });

    state.swiper = swiper;
    sectionInstances.set(sectionId, state);

    bindPauseEvents(sectionEl, state);
    bindLogoTrackDrag(state);
    bindLogoClicks(state);
    setupIntersectionObserver(sectionEl, state);
    updateLogoState(state);
  }

  function destroySection(sectionEl) {
    var sectionId = sectionEl && sectionEl.dataset ? sectionEl.dataset.sectionId : null;
    var state = sectionId ? sectionInstances.get(sectionId) : null;
    if (!state) return;

    state.destroyCallbacks.forEach(function(callback) {
      callback();
    });

    if (state.swiper && !state.swiper.destroyed) {
      state.swiper.destroy(true, false);
    }

    sectionInstances.delete(sectionId);
  }

  function initAll() {
    if (typeof Swiper === 'undefined') {
      window.setTimeout(initAll, 100);
      return;
    }

    document.querySelectorAll('[data-section-type="home-pr-spotlight"]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      var section = event.target.querySelector('[data-section-type="home-pr-spotlight"]');
      if (section) {
        initSection(section);
      }
    });

    document.addEventListener('shopify:section:unload', function(event) {
      var section = event.target.querySelector('[data-section-type="home-pr-spotlight"]');
      if (section) {
        destroySection(section);
      }
    });

    document.addEventListener('shopify:section:reorder', function() {
      Array.prototype.slice.call(sectionInstances.values()).forEach(function(state) {
        destroySection(state.sectionEl);
      });
      initAll();
    });
  }

  window.addEventListener('beforeunload', function() {
    Array.prototype.slice.call(sectionInstances.values()).forEach(function(state) {
      destroySection(state.sectionEl);
    });
  });
})();
